import { useState } from 'react';
import { Button, Spinner } from '@heroui/react';
import { StatusChip, type ChipTone } from '@/components/common/StatusChip';
import { DnsRecordsTable } from './DnsRecordsTable';
import { ZoneSyncModal } from './ZoneSyncModal';
import { WebmailCard } from './WebmailCard';
import { DelegationReadiness } from './DelegationReadiness';
import {
  useCreateDnsZone, useDnsZones, usePublishDnsZone, useValidateDnsZone, useZoneHealth, useZoneRecords,
} from '@/features/domains/hooks';
import type { Delegation, Domain } from '@/features/domains/types';
import { ArrowPathIcon, ArrowsRightLeftIcon, ShieldCheckIcon } from '@/icons';

const DELEGATION: Record<Delegation['state'], { tone: ChipTone; label: string }> = {
  nifty: { tone: 'success', label: 'Live on Nifty DNS' },
  partial: { tone: 'warning', label: 'Mixed nameservers' },
  external: { tone: 'warning', label: 'DNS hosted elsewhere' },
  unknown: { tone: 'neutral', label: 'Delegation unknown' },
};

/** Authoritative DNS lifecycle for one mail domain. Changes stay draft until publish. */
export function ManagedDnsPanel({ domain }: { domain: Domain }) {
  const zones = useDnsZones();
  const zone = zones.data?.find((item) => item.mailDomainId === domain.id);
  const create = useCreateDnsZone();
  const validate = useValidateDnsZone(zone?.id ?? '');
  const publish = usePublishDnsZone(zone?.id ?? '');
  const health = useZoneHealth(zone?.id ?? '');
  const records = useZoneRecords(zone?.id ?? '');
  const [syncOpen, setSyncOpen] = useState(false);

  if (zones.isLoading) return <div className="flex justify-center py-12"><Spinner label="Loading DNS zone…" /></div>;
  if (zones.isError) return (
    <div className="rounded-xl border border-danger/30 bg-danger/5 p-5">
      <p className="text-sm font-medium text-danger">The DNS control plane could not be reached.</p>
      <Button className="mt-3" size="sm" variant="flat" onPress={() => zones.refetch()}>Retry</Button>
    </div>
  );

  if (!zone) return (
    <div className="rounded-xl border border-border bg-surface p-6">
      <h3 className="text-sm font-semibold text-fg">Create the managed DNS zone</h3>
      <p className="mt-2 max-w-2xl text-sm text-fg-muted">
        Nifty will create an authoritative zone and protect its required nameserver records. You can review every record before publishing.
      </p>
      {!domain.verified && (
        <p className="mt-3 rounded-lg border border-warning/30 bg-warning/5 p-3 text-sm text-warning">
          Verify ownership of this domain before creating its managed zone.
        </p>
      )}
      <Button className="mt-4" color="primary" isDisabled={!domain.verified} isLoading={create.isPending}
        onPress={() => create.mutate(domain.id)}>
        Create managed zone
      </Button>
    </div>
  );

  const delegation = health.data?.delegation;
  const delegationChip = health.isLoading ? { tone: 'neutral' as ChipTone, label: 'Checking delegation' }
    : delegation ? DELEGATION[delegation.state]
      : zone.status !== 'active' ? { tone: 'neutral' as ChipTone, label: 'Not published' } : DELEGATION.unknown;
  const drafts = records.data?.filter((r) => r.dirty).length ?? 0;
  // Only the protected nameserver records: the zone carries none of the domain's records yet.
  const incomplete = !!records.data && records.data.every((r) => r.systemManaged);
  return (
    <div className="space-y-4">
      <div className="rounded-xl border border-border bg-surface p-5">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <div className="flex flex-wrap items-center gap-2">
              <h3 className="text-sm font-semibold text-fg">Authoritative zone</h3>
              <StatusChip tone={zone.status === 'active' ? 'success' : 'warning'}>{zone.status}</StatusChip>
              <StatusChip tone={delegationChip.tone}>{delegationChip.label}</StatusChip>
              {drafts > 0 && <StatusChip tone="warning">{drafts} unpublished change{drafts === 1 ? '' : 's'}</StatusChip>}
              <StatusChip tone={zone.dnssecState === 'signed' ? 'success' : 'neutral'}>DNSSEC {zone.dnssecState}</StatusChip>
            </div>
            <p className="mt-2 text-sm text-fg-muted">
              Provider: {zone.provider} · Serial: {zone.serial || 'not published'}
              {zone.lastPublishedAt ? ` · Published ${new Date(zone.lastPublishedAt).toLocaleString()}` : ''}
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button size="sm" variant="flat" startContent={<ArrowsRightLeftIcon className="h-4 w-4" />}
              onPress={() => setSyncOpen(true)}>Sync records</Button>
            <Button size="sm" variant="flat" startContent={<ShieldCheckIcon className="h-4 w-4" />}
              isLoading={validate.isPending} onPress={() => validate.mutate()}>Validate</Button>
            <Button size="sm" color="primary" startContent={<ArrowPathIcon className="h-4 w-4" />}
              isLoading={publish.isPending} onPress={() => publish.mutate()}>Publish changes</Button>
          </div>
        </div>
        {delegation?.state === 'nifty' ? (
          <div className="mt-4 rounded-lg border border-success/25 bg-success/5 p-3 text-xs text-fg-muted">
            The registrar delegates this domain to Nifty: published records are what the internet sees.
          </div>
        ) : (
          <div className="mt-4 rounded-lg border border-warning/25 bg-warning/5 p-3 text-xs text-fg-muted">
            {delegation?.nameservers.length
              ? <>DNS for this domain is currently served by <span className="font-mono text-fg">{delegation.nameservers.join(', ')}</span>. </>
              : null}
            This zone is a prepared copy and is <span className="font-medium text-fg">not live</span> until the registrar lists
            <span className="font-mono text-fg"> {(delegation?.expected.length ? delegation.expected : ['ns1.niftyhosting.co.za', 'ns2.niftyhosting.co.za']).join(' and ')}</span>.
            {' '}Keep the existing DNS host active, and change nameservers only once this zone contains every record the domain needs.
          </div>
        )}
        <DelegationReadiness zone={zone} drafts={drafts} readiness={health.data?.nameservers}
          delegation={delegation} loading={health.isLoading} />
        {incomplete && (
          <div className="mt-3 rounded-lg border border-danger/25 bg-danger/5 p-3 text-sm text-danger" role="alert">
            This zone has only its nameserver records. It is not configured yet. Use <span className="font-medium">Sync records</span> to add the
            domain's mail records and the records it uses today.
          </div>
        )}
        {validate.data && !validate.data.ok && (
          <div className="mt-3 rounded-lg border border-danger/25 bg-danger/5 p-3" role="alert">
            <p className="text-sm font-medium text-danger">Zone validation needs attention</p>
            <ul className="mt-2 space-y-1 text-sm text-danger">
              {validate.data.issues.map((issue) => (
                <li key={`${issue.code}:${issue.message}`}>{issue.message}</li>
              ))}
            </ul>
          </div>
        )}
      </div>
      <DnsRecordsTable domainId={zone.id} />
      <WebmailCard zoneId={zone.id} delegation={delegation?.state} nameservers={delegation?.nameservers} />
      <ZoneSyncModal zoneId={zone.id} isOpen={syncOpen} onClose={() => setSyncOpen(false)} />
    </div>
  );
}
